<template>
  <v-container fluid class="invoice-container">
    <v-skeleton-loader v-if="loading" type="table,list-item-avatar-three-line"></v-skeleton-loader>
    
    <v-card v-else elevation="2" class="invoice-card pa-6 mx-auto" max-width="1000px">
      <!-- Encabezado de la factura -->
      <v-row class="invoice-header mb-6">
        <v-col cols="7">
          <div class="d-flex">
            <v-avatar color="primary" size="55" class="mr-4">
              <v-icon large color="white">mdi-receipt</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold primary--text mb-1">{{ getTipoCFDesc(invoice.Encf) }}</h1>
              <!-- <div class="subtitle-1 grey--text text--darken-1">
                <span v-if="getTipoCFDesc(invoice.TipoeCF)">{{ getTipoCFDesc(invoice.TipoeCF) }}</span>
              </div> -->
            </div>
          </div>
        </v-col>
        <v-col cols="5" class="text-right">
          <div class="invoice-number">
            <div class="text-overline grey--text mb-1">NÚMERO DE COMPROBANTE</div>
            <div class="text-h5 font-weight-medium">{{ invoice.eNCF || 'N/A' }}</div>
          </div>
        </v-col>
      </v-row>

      <v-divider class="mb-6"></v-divider>
      
      <!-- Datos del emisor/receptor -->
      <v-row class="invoice-entities mb-6">
        <v-col cols="12" md="6" class="pr-md-6">
          <v-card outlined class="pa-4 h-100">
            <div class="text-overline mb-2 primary--text">EMISOR</div>
            <div class="text-h6 font-weight-bold mb-2">{{ invoice.Emisor }}</div>
            <div class="body-1">
              <div><strong>RNC:</strong> {{ invoice.RncEmisor }}</div>
              <div v-if="invoice.NombreComercialEmisor"><strong>Nombre Comercial:</strong> {{ invoice.NombreComercialEmisor }}</div>
              <div v-if="invoice.DireccionEmisor"><strong>Dirección:</strong> {{ invoice.DireccionEmisor }}</div>
              <div v-if="invoice.TelefonoEmisor"><strong>Teléfono:</strong> {{ invoice.TelefonoEmisor }}</div>
              <div v-if="invoice.WebSiteEmisor"><strong>Web:</strong> {{ invoice.WebSiteEmisor }}</div>
              <div v-if="invoice.NumeroFacturaInterna"><strong>No. Factura Interna:</strong> {{ invoice.NumeroFacturaInterna }}</div>
            </div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="6" class="pl-md-6">
          <v-card outlined class="pa-4 h-100">
            <div class="text-overline mb-2 primary--text">RECEPTOR</div>
            <div class="text-h6 font-weight-bold mb-2">{{ invoice.Receptor }}</div>
            <div class="body-1">
              <div><strong>RNC:</strong> {{ invoice.RncReceptor }}</div>
              <div v-if="invoice.DireccionComprador"><strong>Dirección:</strong> {{ invoice.DireccionComprador }}</div>
              <div v-if="invoice.CorreoComprador"><strong>Correo:</strong> {{ invoice.CorreoComprador }}</div>
              <div v-if="invoice.CodigoInternoComprador"><strong>Código Interno:</strong> {{ invoice.CodigoInternoComprador }}</div>
              <div v-if="invoice.NumeroOrdenCompra"><strong>No. Orden de Compra:</strong> {{ invoice.NumeroOrdenCompra }}</div>
            </div>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Fechas y tipo de documento -->
      <v-row class="invoice-dates mb-6">
        <v-col cols="12" md="4" class="pr-md-2">
          <v-card flat class="invoice-date-card pa-4">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
              <div>
                <div class="text-overline mb-1">FECHA DE EMISIÓN</div>
                <div class="text-body-1 font-weight-medium">{{ formatDate(invoice.FechaEmision) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4" class="px-md-2">
          <v-card flat class="invoice-date-card pa-4">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-file-document-outline</v-icon>
              <div>
                <div class="text-overline mb-1">TIPO DE DOCUMENTO</div>
                <div class="text-body-1 font-weight-medium">{{ getTipoCFDesc(invoice.TipoeCF) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4" class="pl-md-2">
          <v-card flat class="invoice-date-card pa-4">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
              <div>
                <div class="text-overline mb-1">FECHA LÍMITE DE PAGO</div>
                <div class="text-body-1 font-weight-medium">{{ formatDate(invoice.FechaLimitePago) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Información adicional del documento -->
    
      
      <!-- Tabla de detalles de línea -->
      <div class="invoice-items mb-6">
        <div class="text-overline mb-3 primary--text">DETALLE DE FACTURA</div>
        <v-simple-table class="invoice-table">
          <template v-slot:default>
            <thead>
              <tr class="invoice-table-header">
                <th>Línea</th>
                <th>Código</th>
                <th>Descripción</th>
                <th class="text-right">Cantidad</th>
                <th class="text-right">Precio Unitario</th>
                <th class="text-right">Descuento</th>
                <th>Tipo</th>
                <th>Facturación</th>
                <th class="text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in invoice.DetallesItems" :key="index" class="invoice-item">
                <td>{{ item.numeroLinea || (index + 1) }}</td>
                <td class="font-weight-medium">{{ item.codigo || 'N/A' }}</td>
                <td>{{ item.descripcion || item.nombre || 'N/A' }}</td>
                <td class="text-right">{{ item.cantidad || '0' }}</td>
                <td class="text-right">{{ formatAmount(item.precioUnitario) }}</td>
                <td class="text-right">{{ formatAmount(item.descuento) }}</td>
                <td>{{ item.indicadorBienServicio ? getIndicadorBienServicioDesc(item.indicadorBienServicio) : 'N/A' }}</td>
                <td>{{ item.indicadorFacturacion ? getIndicadorFacturacionDesc(item.indicadorFacturacion) : 'N/A' }}</td>
                <td class="text-right font-weight-medium">{{ formatAmount(item.monto) }}</td>
              </tr>
              <tr v-if="!invoice.DetallesItems || invoice.DetallesItems.length === 0" class="invoice-item">
                <td colspan="9" class="text-center py-5">No hay detalles disponibles</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      
      <!-- Sección de totales -->
      <v-row class="invoice-summary mb-6">
        <v-col cols="12" sm="7">
          <!-- Subtotales adicionales si existen -->
          <v-card outlined class="pa-4 mb-4" v-if="invoice.Subtotales && invoice.Subtotales.length > 0">
            <div class="text-overline mb-3 primary--text">SUBTOTALES</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Descripción</th>
                    <th class="text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(subtotal, index) in invoice.Subtotales" :key="index">
                    <td>{{ subtotal.descripcion || `Subtotal ${subtotal.numero || index + 1}` }}</td>
                    <td class="text-right">{{ formatAmount(subtotal.monto) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
          
          <!-- Impuestos adicionales si existen -->
          <v-card outlined class="pa-4" v-if="invoice.ImpuestosAdicionales && invoice.ImpuestosAdicionales.length > 0">
            <div class="text-overline mb-3 primary--text">IMPUESTOS ADICIONALES</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Tipo</th>
                    <th class="text-right">Tasa</th>
                    <th class="text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(impuesto, index) in invoice.ImpuestosAdicionales" :key="index">
                    <td>{{ getTipoImpuestoDesc(impuesto.tipo) }}</td>
                    <td class="text-right">{{ impuesto.tasa }}%</td>
                    <td class="text-right">{{ formatAmount(impuesto.monto) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
        
        <v-col cols="12" sm="5">
          <v-card outlined class="pa-4 invoice-totals">
            <!-- Montos exentos si existen -->
            <v-row no-gutters dense v-if="invoice.MontoExento">
              <v-col cols="8" class="py-2">
                <div class="body-1 grey--text text--darken-1">Monto Exento</div>
              </v-col>
              <v-col cols="4" class="py-2 text-right">
                <div class="body-1">{{ formatCurrency(invoice.MontoExento) }}</div>
              </v-col>
            </v-row>
            
            <v-row no-gutters dense>
              <v-col cols="8" class="py-2">
                <div class="body-1 grey--text text--darken-1">Subtotal</div>
              </v-col>
              <v-col cols="4" class="py-2 text-right">
                <div class="body-1">{{ formatCurrency(calcSubtotal()) }}</div>
              </v-col>

              <v-col cols="8" class="py-2">
                <div class="body-1 grey--text text--darken-1">Impuestos</div>
              </v-col>
              <v-col cols="4" class="py-2 text-right">
                <div class="body-1">{{ formatCurrency(invoice.MontoImpuesto) }}</div>
              </v-col>
              
              <!-- Avance de pago si existe -->
              <template v-if="invoice.MontoAvancePago">
                <v-col cols="8" class="py-2">
                  <div class="body-1 grey--text text--darken-1">Avance de Pago</div>
                </v-col>
                <v-col cols="4" class="py-2 text-right">
                  <div class="body-1">{{ formatCurrency(invoice.MontoAvancePago) }}</div>
                </v-col>
              </template>
              
              <v-divider class="my-2"></v-divider>

              <v-col cols="8" class="py-2">
                <div class="subtitle-1 font-weight-bold">Total</div>
              </v-col>
              <v-col cols="4" class="py-2 text-right">
                <div class="subtitle-1 font-weight-bold">{{ formatCurrency(invoice.MontoTotal) }}</div>
              </v-col>
              
              <!-- Valor a pagar si es diferente del total -->
              <template v-if="invoice.ValorPagar && invoice.ValorPagar !== invoice.MontoTotal">
                <v-col cols="8" class="py-2 primary--text">
                  <div class="subtitle-1 font-weight-bold">Valor a Pagar</div>
                </v-col>
                <v-col cols="4" class="py-2 text-right primary--text">
                  <div class="subtitle-1 font-weight-bold">{{ formatCurrency(invoice.ValorPagar) }}</div>
                </v-col>
              </template>
            </v-row>
            
            <!-- Información en otra moneda si existe -->
            <template v-if="invoice.OtraMoneda">
              <v-divider class="my-3"></v-divider>
              <div class="text-overline mb-2">EN {{ invoice.OtraMoneda.tipo }}</div>
              <v-row no-gutters dense>
                <v-col cols="8" class="py-1">
                  <div class="body-2 grey--text">Tasa de Cambio</div>
                </v-col>
                <v-col cols="4" class="py-1 text-right">
                  <div class="body-2">{{ invoice.OtraMoneda.tipoCambio }}</div>
                </v-col>
                
                <v-col cols="8" class="py-1">
                  <div class="body-2 grey--text">Total</div>
                </v-col>
                <v-col cols="4" class="py-1 text-right">
                  <div class="body-2">{{ invoice.OtraMoneda.montoTotal }}</div>
                </v-col>
              </v-row>
            </template>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Información Adicional del Documento (colapsada por defecto) -->
      <v-card class="mt-6" outlined>
        <v-expand-transition>
          <div>
            <v-card-title @click="expandInfo = !expandInfo" class="d-flex align-center cursor-pointer">
              <v-icon color="primary" class="mr-2">mdi-information-circle</v-icon>
              <span class="text-subtitle-1 primary--text font-weight-medium">Información Adicional del Documento</span>
              <v-spacer></v-spacer>
              <v-btn icon @click.stop="expandInfo = !expandInfo">
                <v-icon>{{ expandInfo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </v-card-title>
            
            <v-expand-transition>
              <div v-show="expandInfo">
                <v-divider></v-divider>
                <v-card-text>
                  <!-- Primera fila: Tipo de documento, Tipo de ingresos, Tipo de pago -->
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="text-overline grey--text">TIPO DE DOCUMENTO</div>
                      <div class="body-1 mb-3">{{ getTipoCFDesc(invoice.TipoeCF || invoice.Encf) }}</div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="text-overline grey--text">TIPO DE INGRESOS</div>
                      <div class="body-1 mb-3">{{ invoice.TipoIngresos ? getTipoIngresosDesc(invoice.TipoIngresos) : 'N/A' }}</div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="text-overline grey--text">TIPO DE PAGO</div>
                      <div class="body-1 mb-3">{{ invoice.TipoPago ? getTipoPagoDesc(invoice.TipoPago) : 'N/A' }}</div>
                    </v-col>
                  </v-row>
                  
                  <!-- Segunda fila: Vencimiento, Período desde y hasta -->
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="text-overline grey--text">VENCIMIENTO DE SECUENCIA</div>
                      <div class="body-1 mb-3">{{ invoice.FechaVencimiento ? formatDate(invoice.FechaVencimiento) : 'N/A' }}</div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="text-overline grey--text">PERÍODO DESDE</div>
                      <div class="body-1 mb-3">{{ invoice.FechaDesde ? formatDate(invoice.FechaDesde) : 'N/A' }}</div>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <div class="text-overline grey--text">PERÍODO HASTA</div>
                      <div class="body-1 mb-3">{{ invoice.FechaHasta ? formatDate(invoice.FechaHasta) : 'N/A' }}</div>
                    </v-col>
                  </v-row>
                  
                  <!-- Formas de pago si existen -->
                  <div v-if="invoice.FormasPago && invoice.FormasPago.length > 0">
                    <div class="text-overline grey--text mt-3">FORMAS DE PAGO</div>
                    <v-simple-table dense class="mt-1">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>Forma de Pago</th>
                            <th class="text-right">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(formaPago, index) in invoice.FormasPago" :key="index">
                            <td>{{ getFormaPagoDesc(formaPago.forma) }}</td>
                            <td class="text-right">RD$ {{ formatAmount(formaPago.monto) }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </v-card-text>
              </div>
            </v-expand-transition>
          </div>
        </v-expand-transition>
      </v-card>
      
     
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment';
export default {
  name: 'ViewDocumentRecived',
  data() {
    return {
      loading: true,
      expandInfo: false,
      invoice: {},
      itemHeaders: [
        { text: 'Código', value: 'codigo' },
        { text: 'Descripción', value: 'descripcion' },
        { text: 'Cantidad', value: 'cantidad', align: 'end' },
        { text: 'Precio Unitario', value: 'precioUnitario', align: 'end' },
        { text: 'Monto', value: 'monto', align: 'end' }
      ]
    };
  },
  async created() {
    await this.loadInvoice();
  },
  methods: {
    async loadInvoice() {
      this.loading = true;
      const rnc = this.$route.params.rnc;
      const encf = this.$route.params.encf;
      // Obtener factura específica vía API de documentos recibidos
      const filters = `WHERE "RncReceptor"='${rnc}' AND "Encf"='${encf}'`;
      const response = await this.api_Post('accounting/documents_recived_dgii', { filters });
      const invoices = await response.json();
      this.invoice = invoices.length ? invoices[0] : {};
      
      // Parse XML content
      try {
        const parser = new DOMParser();
        const xml = parser.parseFromString(this.invoice.ContenidoXml || '', 'text/xml');
        
        // Extract header information
        const h = xml.querySelector('Encabezado') || xml;
        const idDoc = h.querySelector('IdDoc') || h;
        const emisor = h.querySelector('Emisor') || h;
        const comprador = h.querySelector('Comprador') || h;
        
        // Basic document info
 
        this.invoice.eNCF = idDoc.querySelector('eNCF')?.textContent;
  
        this.invoice.FechaVencimientoSecuencia = idDoc.querySelector('FechaVencimientoSecuencia')?.textContent;
        this.invoice.TipoIngresos = idDoc.querySelector('TipoIngresos')?.textContent;
        this.invoice.TipoPago = idDoc.querySelector('TipoPago')?.textContent;
        this.invoice.FechaLimitePago = idDoc.querySelector('FechaLimitePago')?.textContent;
        
        // Payment forms
        const formasPago = Array.from(idDoc.querySelectorAll('TablaFormasPago > FormaDePago') || []);
        if (formasPago.length > 0) {
          this.invoice.FormasPago = formasPago.map(fp => ({
            forma: fp.querySelector('FormaPago')?.textContent,
            monto: fp.querySelector('MontoPago')?.textContent
          }));
        }
        
        // Dates
        this.invoice.FechaDesde = idDoc.querySelector('FechaDesde')?.textContent;
        this.invoice.FechaHasta = idDoc.querySelector('FechaHasta')?.textContent;
        
        // Emisor (Sender) information
        this.invoice.RncEmisor = emisor.querySelector('RNCEmisor')?.textContent;
        this.invoice.Emisor = emisor.querySelector('RazonSocialEmisor')?.textContent;
        this.invoice.NombreComercialEmisor = emisor.querySelector('NombreComercial')?.textContent;
        this.invoice.DireccionEmisor = emisor.querySelector('DireccionEmisor')?.textContent;
        this.invoice.TelefonoEmisor = emisor.querySelector('TablaTelefonoEmisor > TelefonoEmisor')?.textContent;
        this.invoice.WebSiteEmisor = emisor.querySelector('WebSite')?.textContent;
        this.invoice.NumeroFacturaInterna = emisor.querySelector('NumeroFacturaInterna')?.textContent;
        this.invoice.FechaEmision = emisor.querySelector('FechaEmision')?.textContent;
        
        // Comprador (Buyer) information
        this.invoice.RncReceptor = comprador.querySelector('RNCComprador')?.textContent;
        this.invoice.Receptor = comprador.querySelector('RazonSocialComprador')?.textContent;
        this.invoice.DireccionComprador = comprador.querySelector('DireccionComprador')?.textContent;
        this.invoice.CorreoComprador = comprador.querySelector('CorreoComprador')?.textContent;
        this.invoice.CodigoInternoComprador = comprador.querySelector('CodigoInternoComprador')?.textContent;
        this.invoice.NumeroOrdenCompra = comprador.querySelector('NumeroOrdenCompra')?.textContent;
        
        // Totals
        const t = h.querySelector('Totales') || xml;
        this.invoice.MontoExento = t.querySelector('MontoExento')?.textContent;
        this.invoice.MontoImpuesto = t.querySelector('MontoImpuestoAdicional')?.textContent;
        this.invoice.MontoTotal = t.querySelector('MontoTotal')?.textContent;
        this.invoice.MontoPeriodo = t.querySelector('MontoPeriodo')?.textContent;
        this.invoice.SaldoAnterior = t.querySelector('SaldoAnterior')?.textContent;
        this.invoice.MontoAvancePago = t.querySelector('MontoAvancePago')?.textContent;
        this.invoice.ValorPagar = t.querySelector('ValorPagar')?.textContent;
        
        // Other currency information
        const otraMoneda = h.querySelector('OtraMoneda');
        if (otraMoneda) {
          this.invoice.OtraMoneda = {
            tipo: otraMoneda.querySelector('TipoMoneda')?.textContent,
            tipoCambio: otraMoneda.querySelector('TipoCambio')?.textContent,
            montoExento: otraMoneda.querySelector('MontoExentoOtraMoneda')?.textContent,
            montoTotal: otraMoneda.querySelector('MontoTotalOtraMoneda')?.textContent
          };
        }
        
        // Tax details
        const impuestosAdicionales = Array.from(t.querySelectorAll('ImpuestosAdicionales > ImpuestoAdicional') || []);
        if (impuestosAdicionales.length > 0) {
          this.invoice.ImpuestosAdicionales = impuestosAdicionales.map(imp => ({
            tipo: imp.querySelector('TipoImpuesto')?.textContent,
            tasa: imp.querySelector('TasaImpuestoAdicional')?.textContent,
            monto: imp.querySelector('OtrosImpuestosAdicionales')?.textContent
          }));
        }
        
        // Line items
        const items = Array.from(xml.querySelectorAll('DetallesItems > Item') || []);
        if (items.length > 0) {
          this.invoice.DetallesItems = items.map(item => {
            // Get item codes if available
            let codigo = '';
            const codigosItem = item.querySelector('TablaCodigosItem > CodigosItem');
            if (codigosItem) {
              const tipoCodigo = codigosItem.querySelector('TipoCodigo')?.textContent;
              const codigoItem = codigosItem.querySelector('CodigoItem')?.textContent;
              codigo = codigoItem || '';
            } else {
              codigo = item.querySelector('CodigoItem')?.textContent || '';
            }
            
            // Extract other item details
            return {
              numeroLinea: item.querySelector('NumeroLinea')?.textContent,
              codigo: codigo,
              indicadorFacturacion: item.querySelector('IndicadorFacturacion')?.textContent,
              nombre: item.querySelector('NombreItem')?.textContent,
              descripcion: item.querySelector('DescripcionItem')?.textContent,
              cantidad: item.querySelector('CantidadItem')?.textContent,
              precioUnitario: item.querySelector('PrecioUnitarioItem')?.textContent,
              descuento: item.querySelector('DescuentoMonto')?.textContent,
              monto: item.querySelector('MontoItem')?.textContent,
              indicadorBienServicio: item.querySelector('IndicadorBienoServicio')?.textContent
            };
          });
        }
        
        // Subtotals
        const subtotales = Array.from(xml.querySelectorAll('Subtotales > Subtotal') || []);
        if (subtotales.length > 0) {
          this.invoice.Subtotales = subtotales.map(sub => ({
            numero: sub.querySelector('NumeroSubTotal')?.textContent,
            descripcion: sub.querySelector('DescripcionSubtotal')?.textContent,
            orden: sub.querySelector('Orden')?.textContent,
            impuesto: sub.querySelector('SubTotalImpuestoAdicional')?.textContent,
            exento: sub.querySelector('SubTotalExento')?.textContent,
            monto: sub.querySelector('MontoSubTotal')?.textContent
          }));
        }
        
        // Signature date
        this.invoice.FechaHoraFirma = xml.querySelector('FechaHoraFirma')?.textContent;
      } catch (error) {
        console.error('Error parsing XML:', error);
      }
      
      this.loading = false;
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD [de] MMMM [del] YYYY');
    },
    formatCurrency(value) {
      if (!value) return 'RD$ 0.00';
      return `RD$ ${parseFloat(value).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatAmount(value) {
      if (!value) return '0.00';
      return parseFloat(value).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    getTipoCFDesc(code) {
      // Si el código es un eNCF (comienza con E), extraer los 2 dígitos después de la E
      if (typeof code === 'string' && code.startsWith('E')) {
        code = code.substring(1, 3);
      }
      
      const tipos = {
        '31': 'Factura de Crédito Fiscal Electrónica',
        '32': 'Factura de Consumo Electrónica',
        '33': 'Nota de Débito Electrónica',
        '34': 'Nota de Crédito Electrónica',
        '41': 'Compras Electrónico',
        '43': 'Gastos Menores Electrónico',
        '44': 'Regímenes Especiales Electrónico',
        '45': 'Gubernamental Electrónico',
        '46': 'Comprobante de Exportaciones Electrónico',
        '47': 'Comprobante para Pagos al Exterior Electrónico'
      };
      return tipos[code] || code;
    },
    getTipoIngresosDesc(code) {
      const tipos = {
        '01': 'Ingresos por operaciones (No financieros)',
        '02': 'Ingresos Financieros',
        '03': 'Ingresos Extraordinarios',
        '04': 'Ingresos por Arrendamientos',
        '05': 'Ingresos por Venta de Activo Depreciable',
        '06': 'Otros Ingresos'
      };
      return tipos[code] || code;
    },
    getTipoPagoDesc(code) {
      const tipos = {
        '1': 'Contado',
        '2': 'Crédito',
        '3': 'Gratuito'
      };
      return tipos[code] || code;
    },
    getFormaPagoDesc(code) {
      const formas = {
        '1': 'Efectivo',
        '2': 'Cheque/Transferencia/Depósito',
        '3': 'Tarjeta de Débito/Crédito',
        '4': 'Venta a Crédito',
        '5': 'Bonos o Certificados de regalo',
        '6': 'Permuta',
        '7': 'Nota de crédito',
        '8': 'Otras Formas de pago'
      };
      return formas[code] || code;
    },
    getTipoCuentaPagoDesc(code) {
      const tipos = {
        'CT': 'Cuenta Corriente',
        'AH': 'Ahorro',
        'OT': 'Otra'
      };
      return tipos[code] || code;
    },
    getIndicadorFacturacionDesc(code) {
      const indicadores = {
        '0': 'No Facturable (18%)',
        '1': 'ITBIS 1 (18%)',
        '2': 'ITBIS 2 (16%)',
        '3': 'ITBIS 3 (0%)',
        '4': 'Exento (E)'
      };
      return indicadores[code] || code;
    },
    getIndicadorBienServicioDesc(code) {
      const indicadores = {
        '1': 'Bien',
        '2': 'Servicio'
      };
      return indicadores[code] || code;
    },
    getTipoImpuestoDesc(code) {
      const impuestos = {
        '001': 'Propina Legal',
        '002': 'Contribución al Desarrollo de las Telecomunicaciones',
        '003': 'Servicios Seguros en general',
        '004': 'Servicios de Telecomunicaciones',
        '005': 'Expedición de la primera placa',
        '006': 'Cerveza'
        // Más impuestos pueden ser añadidos según sea necesario
      };
      return impuestos[code] || code;
    },
    getUnidadMedidaDesc(code) {
      const unidades = {
        'UND': 'Unidad',
        'LTR': 'Litro',
        'GAL': 'Galón',
        'ONZ': 'Onza',
        'LBS': 'Libra',
        'KLG': 'Kilogramo',
        'GRM': 'Gramo',
        'TON': 'Tonelada',
        'PQT': 'Paquete',
        'BOT': 'Botella',
        'LAT': 'Lata',
        'CJ': 'Caja',
        'MTR': 'Metro',
        'CM': 'Centímetro',
        'MM': 'Milímetro',
        'PIE': 'Pie',
        'PUL': 'Pulgada',
        'JGO': 'Juego',
        'SERV': 'Servicio',
        'DOC': 'Docena',
        'PAR': 'Par',
        'QTL': 'Quintal',
        'HR': 'Hora',
        'DIA': 'Día',
        'MES': 'Mes',
        'OTR': 'Otro'
      };
      return unidades[code] || code;
    },
    getProvinciaDesc(code) {
      const provincias = {
        '01': 'DISTRITO NACIONAL',
        '02': 'AZUA',
        '03': 'BAORUCO',
        '04': 'BARAHONA',
        '05': 'DAJABON',
        '06': 'DUARTE',
        '07': 'ELIAS PIÑA',
        '08': 'EL SEIBO',
        '09': 'ESPAILLAT',
        '10': 'INDEPENDENCIA',
        '11': 'LA ALTAGRACIA',
        '12': 'LA ROMANA',
        '13': 'LA VEGA',
        '14': 'MARIA TRINIDAD SANCHEZ',
        '15': 'MONTE CRISTI',
        '16': 'PEDERNALES',
        '17': 'PERAVIA',
        '18': 'PUERTO PLATA',
        '19': 'HERMANAS MIRABAL',
        '20': 'SAMANA',
        '21': 'SAN CRISTOBAL',
        '22': 'SAN JUAN',
        '23': 'SAN PEDRO DE MACORIS',
        '24': 'SANCHEZ RAMIREZ',
        '25': 'SANTIAGO',
        '26': 'SANTIAGO RODRIGUEZ',
        '27': 'VALVERDE',
        '28': 'MONSEÑOR NOUEL',
        '29': 'MONTE PLATA',
        '30': 'HATO MAYOR',
        '31': 'SAN JOSE DE OCOA',
        '32': 'SANTO DOMINGO'
      };
      return provincias[code] || code;
    },
    calcSubtotal() {
      if (!this.invoice.MontoTotal || !this.invoice.MontoImpuesto) {
        return this.invoice.MontoTotal;
      }
      return parseFloat(this.invoice.MontoTotal) - parseFloat(this.invoice.MontoImpuesto);
    }
  }
};
</script>

<style scoped>
/* Estilos personalizados */
.invoice-container {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 2rem;
}

.invoice-card {
  background-color: white;
  border-radius: 8px;
}

.invoice-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.invoice-table-header {
  background-color: #f5f5f5;
}

.invoice-item td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 12px;
  padding-bottom: 12px;
}

.invoice-number {
  padding: 8px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.03);
  display: inline-block;
  min-width: 200px;
}

.invoice-date-card {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
}

.invoice-totals {
  background-color: #f9f9f9;
}
</style>